import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TranslatableInput.css';

/**
 * TranslatableInput - Input field with language tabs for ES/EN/PT
 *
 * @param {string} label - Field label (e.g., "Name", "Description")
 * @param {string} type - Input type: "text" | "textarea"
 * @param {string} value - Spanish value (primary)
 * @param {function} onChange - Callback when Spanish value changes
 * @param {object} translations - { en: "...", pt: "..." }
 * @param {function} onTranslationsChange - Callback when translations change
 * @param {boolean} required - Whether field is required
 */
const LANGS = ['es', 'en', 'pt'];

function TranslatableInput({
  label,
  labelKey,
  type = 'text',
  value,
  onChange,
  translations = {},
  onTranslationsChange,
  required = false,
  error,
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('es');

  const hasContent = (lang) => {
    if (lang === 'es') return !!value?.trim();
    return !!translations[lang]?.trim();
  };

  const handleTranslationChange = (lang, newValue) => {
    const updated = { ...translations, [lang]: newValue };
    onTranslationsChange(updated);
  };

  const handleTabKeyDown = (e) => {
    const idx = LANGS.indexOf(activeTab);
    let nextIdx = -1;
    if (e.key === 'ArrowRight') {
      nextIdx = (idx + 1) % LANGS.length;
    } else if (e.key === 'ArrowLeft') {
      nextIdx = (idx - 1 + LANGS.length) % LANGS.length;
    } else if (e.key === 'Home') {
      nextIdx = 0;
    } else if (e.key === 'End') {
      nextIdx = LANGS.length - 1;
    }
    if (nextIdx >= 0) {
      e.preventDefault();
      setActiveTab(LANGS[nextIdx]);
      e.currentTarget.parentElement.querySelectorAll('[role="tab"]')[nextIdx]?.focus();
    }
  };

  const getPlaceholder = (lang) => {
    if (lang === 'es') return '';
    const field = labelKey ? t(labelKey, { lng: lang }) : label;
    return t('common.translationPlaceholder', { lng: lang, field, lang: lang.toUpperCase() });
  };

  const renderInput = (lang) => {
    const currentValue = lang === 'es' ? value : translations[lang] || '';
    const handleChange = (e) => {
      if (lang === 'es') {
        onChange(e.target.value);
      } else {
        handleTranslationChange(lang, e.target.value);
      }
    };

    const errorClass = error && lang === 'es' ? 'input-error' : '';

    if (type === 'textarea') {
      return (
        <textarea
          value={currentValue}
          onChange={handleChange}
          required={required && lang === 'es'}
          placeholder={getPlaceholder(lang)}
          className={errorClass}
        />
      );
    }

    return (
      <input
        type="text"
        value={currentValue}
        onChange={handleChange}
        required={required && lang === 'es'}
        placeholder={getPlaceholder(lang)}
        className={errorClass}
      />
    );
  };

  const tabId = label ? label.replace(/\s+/g, '-').toLowerCase() : 'translatable';

  return (
    <div className={`translatable-input${error && activeTab === 'es' ? ' has-error' : ''}`}>
      <label>
        {label} {required && <span className="required">*</span>}
      </label>

      <div className="language-tabs" role="tablist" aria-label={t('menu.languageTabs')}>
        {LANGS.map((lang) => (
          <button
            key={lang}
            type="button"
            role="tab"
            id={`${tabId}-tab-${lang}`}
            aria-selected={activeTab === lang}
            aria-controls={`${tabId}-panel-${lang}`}
            tabIndex={activeTab === lang ? 0 : -1}
            className={`tab ${activeTab === lang ? 'active' : ''}`}
            onClick={() => setActiveTab(lang)}
            onKeyDown={handleTabKeyDown}
          >
            {lang === 'es' ? '🇪🇸 ES' : lang === 'en' ? '🇬🇧 EN' : '🇧🇷 PT'}
            <span
              className={`tab-dot ${hasContent(lang) ? 'tab-dot--filled' : 'tab-dot--empty'}`}
            />
          </button>
        ))}
      </div>

      <div
        className="tab-content"
        role="tabpanel"
        id={`${tabId}-panel-${activeTab}`}
        aria-labelledby={`${tabId}-tab-${activeTab}`}
      >
        {renderInput(activeTab)}
      </div>

      {error && activeTab === 'es' && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}

      {activeTab !== 'es' && <small className="help-text">{t('translationHelperText')}</small>}
    </div>
  );
}

export default TranslatableInput;
